import React from "react";
import { Mic, Square, Check, Sparkles, Loader2 } from "lucide-react";

/**
 * SectionBottomBar — a slim persistent bottom bar rendered ONLY on mobile
 * viewports (hidden md+). Keeps the three primary Section actions
 * (Mic · Save · Reflect) inside thumb reach without scrolling, and
 * respects the iOS home-indicator safe area.
 *
 * Purely presentational — parent Section.jsx passes the same handlers
 * used by the in-page buttons.
 */
export default function SectionBottomBar({
  promptId,
  hasDraft,
  saving,
  reflecting,
  recording,
  transcribing,
  savedAlready,
  onSave,
  onReflect,
  onStartRecord,
  onStopRecord,
}) {
  const canAct = hasDraft && !saving && !reflecting && !recording && !transcribing;
  return (
    <div
      data-testid={`section-bottom-bar-${promptId}`}
      className="fixed left-0 right-0 bottom-0 z-30 md:hidden bg-obsidian-900/95 backdrop-blur-lg border-t border-obsidian-600 pb-safe"
    >
      <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
        {/* Mic — primary voice affordance */}
        {!recording ? (
          <button
            type="button"
            data-testid={`mobile-mic-${promptId}`}
            onClick={onStartRecord}
            disabled={transcribing || saving || reflecting}
            aria-label="Record your answer"
            title="Record"
            className="rounded-full bg-terracotta hover:bg-terracotta-deep text-bone-100 h-14 w-14 shrink-0 flex items-center justify-center shadow-[0_0_0_1px_rgba(184,90,68,0.35)] transition-colors disabled:opacity-50"
          >
            {transcribing ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              <Mic className="h-6 w-6" />
            )}
          </button>
        ) : (
          <button
            type="button"
            data-testid={`mobile-mic-stop-${promptId}`}
            onClick={onStopRecord}
            aria-label="Stop and transcribe"
            title="Stop"
            className="rounded-full bg-terracotta hover:bg-terracotta-deep text-bone-100 h-14 w-14 shrink-0 flex items-center justify-center animate-breathe shadow-[0_0_0_2px_rgba(184,90,68,0.45)] transition-colors"
          >
            <Square className="h-5 w-5 fill-current" />
          </button>
        )}

        {/* Save */}
        <button
          type="button"
          data-testid={`mobile-save-${promptId}`}
          onClick={onSave}
          disabled={!canAct}
          className="flex-1 rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-[0.7rem] h-12 flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          {savedAlready ? "Update" : "Save"}
        </button>

        {/* Reflect */}
        <button
          type="button"
          data-testid={`mobile-reflect-${promptId}`}
          onClick={onReflect}
          disabled={!canAct}
          className="flex-1 rounded-none border border-obsidian-600 hover:border-terracotta bg-transparent text-bone-200 hover:text-terracotta font-sans uppercase tracking-wider text-[0.7rem] h-12 flex items-center justify-center gap-2 transition-colors disabled:opacity-40"
        >
          {reflecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Reflect
        </button>
      </div>
    </div>
  );
}
